import type {
  ContentStudioChecklist219,
  ContentStudioEntry219,
  ContentStudioVariant219,
  EconomicArticle,
} from './models';

export const contentStudioWorkflowLabels219: Record<ContentStudioEntry219['workflowStatus'], string> = {
  candidate: '기사 후보',
  draft: '초안',
  review: '검수 중',
  approved: '승인',
  scheduled: '예약',
  published: '게시',
  archived: '보관',
};

export const contentStudioChecklistLabels219: Record<keyof ContentStudioChecklist219, string> = {
  sourceVerified: '출처·기준일 확인',
  factsVerified: '숫자·사실 확인',
  ageAppropriate: '연령 적합성 확인',
  quizClear: '정답·오답 설명 확인',
  missionSafe: '생활 미션 안전성 확인',
  rewardBalanced: '보상 수준 확인',
};

export const contentStudioDifficultyLabels219: Record<ContentStudioVariant219['difficulty'], string> = {
  1: '가볍게',
  2: '기본',
  3: '깊이 있게',
};

function fallbackVariant(article: EconomicArticle, difficulty: 1 | 2 | 3): ContentStudioVariant219 {
  const prefix = difficulty === 1 ? '생활 속에서' : difficulty === 2 ? '원인과 결과를 연결해' : '시장과 정책의 관점까지 넓혀';
  return {
    difficulty,
    title: article.title,
    summary: article.childSummary,
    explanation: article.keyPoints.slice(0, 3),
    quizPrompt: '이 기사의 핵심 변화와 가장 가까운 설명은 무엇인가요?',
    quizChoices: [article.keyPoints[0] ?? article.childSummary, '가격은 어떤 상황에서도 변하지 않아요.', '경제 활동은 생활과 관계없어요.'],
    correctChoiceIndex: 0,
    quizExplanation: article.keyPoints[0] ?? article.childSummary,
    reasoningPrompt: `${prefix} 이 변화가 우리 선택에 어떤 영향을 주는지 적어 보세요.`,
    mission: difficulty === 1 ? '오늘 본 가격표 한 장을 비교해 보세요.' : difficulty === 2 ? '가족과 가격이 달라진 이유를 한 가지 이야기해 보세요.' : '기사의 변화가 가계·기업·정부에 미칠 영향을 각각 한 줄로 정리해 보세요.',
  };
}

export function createContentStudioEntry219(article: EconomicArticle, now = new Date().toISOString()): ContentStudioEntry219 {
  const published = article.status === 'published';
  const review = article.status === 'review';
  const completeChecklist: ContentStudioChecklist219 = {
    sourceVerified: published,
    factsVerified: published,
    ageAppropriate: published,
    quizClear: published,
    missionSafe: published,
    rewardBalanced: published,
  };
  return {
    id: `studio_${article.id}`,
    articleId: article.id,
    category: article.conceptIds[0] ?? 'economy',
    workflowStatus: published ? 'published' : review ? 'review' : article.status === 'scheduled' ? 'scheduled' : article.status === 'archived' ? 'archived' : 'draft',
    targetDate: article.scheduledAt?.slice(0, 16),
    targetRegions: ['전국'],
    variants: ([1, 2, 3] as const).map((difficulty) => fallbackVariant(article, difficulty)),
    rewardPlan: published
      ? { points: 23, iceBricks: 1, puzzlePieces: 0, specialBrickType: 'none' }
      : { points: 20, iceBricks: 0, puzzlePieces: 1, specialBrickType: 'none' },
    checklist: completeChecklist,
    reviewNote: published ? '출처·난이도·보상 검수를 완료했습니다.' : '출처와 퀴즈 해설을 추가로 확인해 주세요.',
    reviewedBy: article.reviewedBy,
    reviewedAt: article.publishedAt,
    demoPinned: published,
    updatedAt: article.updatedAt || now,
  };
}

export function contentStudioVariant219(entry: ContentStudioEntry219, difficulty: 1 | 2 | 3): ContentStudioVariant219 {
  return entry.variants.find((variant) => variant.difficulty === difficulty) ?? entry.variants[0];
}

export function contentStudioReviewCompletion219(entry: ContentStudioEntry219): number {
  const checklistValues = Object.values(entry.checklist);
  const checklistDone = checklistValues.filter(Boolean).length;
  const variantDone = entry.variants.filter((variant) => variant.title.trim() && variant.summary.trim() && variant.explanation.filter(Boolean).length >= 2 && variant.quizPrompt.trim() && variant.quizChoices.filter(Boolean).length >= 2 && variant.correctChoiceIndex >= 0 && variant.correctChoiceIndex < variant.quizChoices.length && variant.quizExplanation.trim() && variant.reasoningPrompt.trim() && variant.mission.trim()).length;
  return Math.round(((checklistDone + variantDone) / (checklistValues.length + 3)) * 100);
}

export function contentStudioPrimaryRewardCount219(entry: ContentStudioEntry219): number {
  const reward = entry.rewardPlan;
  return [reward.points > 0, reward.iceBricks > 0, reward.puzzlePieces > 0, reward.specialBrickType !== 'none'].filter(Boolean).length;
}

export function contentStudioWarnings219(entry: ContentStudioEntry219): string[] {
  const warnings: string[] = [];
  if (contentStudioPrimaryRewardCount219(entry) > 2) warnings.push('한 활동의 주요 보상은 두 종류 이하가 권장됩니다.');
  if (entry.workflowStatus === 'scheduled' && !entry.targetDate) warnings.push('예약 게시일을 입력해 주세요.');
  if (['approved', 'scheduled', 'published'].includes(entry.workflowStatus) && contentStudioReviewCompletion219(entry) < 100) warnings.push('모든 난이도와 검수 체크리스트를 완료해야 승인·게시할 수 있습니다.');
  if (entry.targetRegions.length === 0) warnings.push('노출 지역을 한 곳 이상 설정해 주세요.');
  return warnings;
}

export function contentStudioCanPublish219(entry: ContentStudioEntry219): boolean {
  return contentStudioReviewCompletion219(entry) === 100
    && contentStudioPrimaryRewardCount219(entry) <= 2
    && entry.targetRegions.length > 0
    && (entry.workflowStatus !== 'scheduled' || Boolean(entry.targetDate));
}
